import {
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  isDevMode,
} from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { provideRouter, withPreloading, withInMemoryScrolling } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';

import { initializeAnalytics } from './core/analytics/analytics.initializer';
import { LanguageService } from './core/services/language.service';
import { SelectivePreloadingStrategy } from './core/services/selective-preloading.strategy';
import { provideTelemetry } from './core/telemetry/telemetry.providers';
import { acceptLanguageHeaderInterceptor } from './interceptors/accept-language-header.interceptor';
import { baseUrlInterceptor } from './interceptors/base-url.interceptor';
import { featureFlagsReducer } from './features/feature-management/store/reducers/feature-flags.reducer';
import { FeatureFlagsEffects } from './features/feature-management/store/effects/feature-flags.effects';
import { msalAuthReducer } from './features/msal-auth/store/reducers/msal-auth.reducer';
import { MsalAuthEffects } from './features/msal-auth/store/effects/msal-auth.effects';
import { msalAuthInterceptor } from './features/msal-auth/interceptors/msal-auth.interceptor';
import { initializeMsal } from './features/msal-auth/msal-initializer';
import { provideMsal } from './features/msal-auth/msal.providers';
import { SessionEffects } from './features/session/store/effects/session.effects';
import { sessionReducer } from './features/session/store/reducers/session.reducer';
import { routes } from './app.routes';

export function initializeLanguage(): Promise<void> {
  const languageService = inject(LanguageService);
  return languageService.initializeApp();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideClientHydration(withEventReplay()),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        baseUrlInterceptor,
        acceptLanguageHeaderInterceptor,
        msalAuthInterceptor,
      ]),
    ),
    ...provideMsal(),
    ...provideTelemetry(),
    provideEffects(FeatureFlagsEffects, SessionEffects, MsalAuthEffects),
    provideStore({
      featureFlags: featureFlagsReducer,
      session: sessionReducer,
      msalAuth: msalAuthReducer,
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      autoPause: true,
      trace: false,
      traceLimit: 75,
    }),
    provideRouter(
      routes,
      withPreloading(SelectivePreloadingStrategy),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    provideZonelessChangeDetection(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    provideAppInitializer(initializeMsal),
    provideAppInitializer(initializeLanguage),
    provideAppInitializer(initializeAnalytics),
  ],
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
